/**
 * A system's documents, from ant's `listDocuments`.
 *
 * Replaces admin_api's `GET /system/:gid/documents`. In ant a document belongs
 * to a system or to an organization; this lists one system's. Ant shows them
 * to every admin of that system or of its organizations (`document:read`) and
 * leaves archived ones out. A document's `url` is a signed link that is good
 * for at least an hour; it is null while ant's document storage is not
 * configured.
 *
 * Note: Run `npm run relay` after editing the GraphQL query.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useDocumentsRelayQuery as UseDocumentsRelayQuery,
  useDocumentsRelayQuery$data,
  DocumentFilterInput,
  DocumentSortInput,
} from './__generated__/useDocumentsRelayQuery.graphql';
import type { useDocumentsRelayUrlQuery as UseDocumentsRelayUrlQuery } from './__generated__/useDocumentsRelayUrlQuery.graphql';

const DocumentsQuery = graphql`
  query useDocumentsRelayQuery(
    $filter: DocumentFilterInput!
    $sort: [DocumentSortInput!]!
    $first: Int!
    $after: String
  ) @throwOnFieldError {
    listDocuments(filter: $filter, sort: $sort, first: $first, after: $after) {
      count
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          description
          category
          contentType
          byteSize
          url
          insertedAt
        }
      }
    }
  }
`;

const DocumentUrlQuery = graphql`
  query useDocumentsRelayUrlQuery($id: ID!) @throwOnFieldError {
    getDocument(id: $id) {
      url
    }
  }
`;

/** A document as the Documents table shows it. */
export interface Document {
  id: string;
  name: string;
  description: string | null;
  category: string;
  type: 'pdf' | 'image';
  contentType: string;
  /** Null for documents copied from admin_api that never recorded a size. */
  size: number | null;
  /** Null while ant's document storage is off. */
  url: string | null;
  uploaded_at: string;
}

type Edge = NonNullable<
  NonNullable<useDocumentsRelayQuery$data['listDocuments']>['edges']
>[number];

export type { DocumentSortInput };

interface DocumentQueryOptions {
  first: number;
  after: string | null;
  search: string;
  sort: DocumentSortInput[];
}

export interface DocumentPage {
  documents: Document[];
  count: number;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
}

/** The React Query key prefix of every document list. */
export const DOCUMENTS_QUERY_KEY = 'documents';

export async function fetchDocuments(
  systemId: string,
  { first, after, search, sort }: DocumentQueryOptions
): Promise<DocumentPage> {
  const filter: DocumentFilterInput = { systemId: { eq: systemId } };
  if (search.trim()) {
    // Treat SQL LIKE wildcards as literal characters in the user's search.
    const pattern = `%${search.trim().replace(/[\\%_]/g, '\\$&')}%`;
    filter.or = [{ name: { ilike: pattern } }, { description: { ilike: pattern } }];
  }

  const result: useDocumentsRelayQuery$data | undefined =
    await fetchQuery<UseDocumentsRelayQuery>(
      relayEnvironment,
      DocumentsQuery,
      { filter, sort, first, after },
      { fetchPolicy: 'network-only' }
    ).toPromise();

  const page = result?.listDocuments;
  if (!page?.edges || !page.pageInfo || page.count == null) {
    throw new Error('Could not load documents. Please try again.');
  }
  if (page.pageInfo.hasNextPage && !page.pageInfo.endCursor) {
    throw new Error('Could not load the next document page. Please try again.');
  }

  return {
    documents: page.edges.map(
      ({ node }: Edge): Document => ({
        id: node.id,
        name: node.name,
        description: node.description ?? null,
        category: node.category,
        type: node.contentType === 'application/pdf' ? 'pdf' : 'image',
        contentType: node.contentType,
        size: node.byteSize ?? null,
        url: node.url ?? null,
        uploaded_at: node.insertedAt,
      })
    ),
    count: page.count,
    pageInfo: {
      hasNextPage: page.pageInfo.hasNextPage,
      endCursor: page.pageInfo.endCursor ?? null,
    },
  };
}

/** Fetch at the time of use: links in a long-lived list may have expired. */
export async function fetchDocumentUrl(id: string): Promise<string> {
  const result = await fetchQuery<UseDocumentsRelayUrlQuery>(
    relayEnvironment,
    DocumentUrlQuery,
    { id },
    { fetchPolicy: 'network-only' }
  ).toPromise();

  if (!result?.getDocument) throw new Error('This document is no longer available.');
  if (!result.getDocument.url) {
    throw new Error('Document storage is currently unavailable.');
  }
  return result.getDocument.url;
}

export function useDocuments(
  systemId: string | undefined,
  options: DocumentQueryOptions & { enabled?: boolean }
) {
  const { enabled, ...query } = options;
  return useQuery({
    queryKey: [DOCUMENTS_QUERY_KEY, systemId, query],
    queryFn: () => fetchDocuments(systemId as string, query),
    enabled: Boolean(systemId) && enabled !== false,
    placeholderData: keepPreviousData,
  });
}
